'use client'

import React from 'react'
import { Space, Table, Typography } from 'antd'
import useSWR from 'swr'
import type { TableProps } from 'antd'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { fetchInstanceList } from '@/service/instance'
import Status from '@/app/components/mlops/Status'
import ImageLink from '@/app/components/mlops/ImageLink'
import ResourceSelectorDesc from '@/app/components/mlops/ResourceSelectorDesc'
import PermissionTag from '@/app/components/mlops/PermissionTag'
import Datetime from '@/app/components/mlops/Datetime'
import TruncatedId from '@/app/components/mlops/TruncatedId'

const { Text } = Typography

const InstanceDisplayList: React.FC = () => {
  const { t } = useTranslation()

  const {
    data: { data: instances },
    isLoading,
  } = useSWR(
    {
      url: 'instances',
      params: {
        page: 1,
        limit: 5, // 只获取5条数据
        search: '',
      },
    },
    fetchInstanceList,
    {
      fallbackData: {
        data: [],
        has_more: false,
        total: 0,
        page: 0,
        limit: 0,
      },
    },
  )

  const columns: TableProps['columns'] = [
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.name')}</span>,
      dataIndex: 'id',
      width: 220,
      fixed: 'left',
      render: (text: string, record) => (
        <Space size={0} direction='vertical'>
          <Link
            className='text-[#1677ff] hover:text-[#69b1ff]'
            href={`/ecs/instances/${text}${record.status === 'init' ? '/edit' : ''}`}>
            <Typography.Link>{record.name || '--'}</Typography.Link>
          </Link>
          <TruncatedId id={text} />
        </Space>
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.status')}</span>,
      dataIndex: 'status',
      width: 120,
      render: (text: any) => <Status value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.permission')}</span>,
      dataIndex: 'permission',
      width: 100,
      render: (text: any) => <PermissionTag value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.resourceSpec')}</span>,
      dataIndex: 'resource_selector_id',
      width: 200,
      render: (text: any, record) => (
        <ResourceSelectorDesc resource_selector={record.resource_selector} />
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.image')}</span>,
      dataIndex: 'image',
      width: 220,
      render: (text, record) => <ImageLink image={record.image} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.createdAt')}</span>,
      dataIndex: 'created_at',
      width: 180,
      render: (text: any, record) => <Datetime value={record.created_at} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('table.columns.updatedAt')}</span>,
      dataIndex: 'updated_at',
      width: 180,
      render: (text: any, record) => <Datetime value={record.updated_at} />,
    },
  ]

  return (
    <Table
      loading={isLoading}
      columns={columns}
      dataSource={instances?.map((v: { id: any }) => ({ ...v, key: v.id }))}
      locale={{
        emptyText: t('table.empty_text') as string,
      }}
      scroll={{ x: 1200 }}
      pagination={false} // 禁用分页
    />
  )
}

export default InstanceDisplayList
